import { Grid } from '@mui/material';
import { BarChart as MuiBarChart } from '@mui/x-charts';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { sum } from '../../../../../../utils';
import { SortTypes } from '../../../../../types';
import { TableView } from '../../../../tableview/TableView';
import { useElementWidth } from '../../common/utils';
import { ChartHeader } from '../common/chart-header/ChartHeader';
import { AxisConfigType, BarChartLayouts } from '../common/common-chart-types';
import {
  getChartLegendConfig,
  getChartNoDataOverlay,
} from '../common/common-chart-utils/common-chart-configs';
import {
  CHART_CONTENT_HEIGHT,
  defaultChartColors,
} from '../common/common-chart-utils/common-chart-styles';
import { getBarChartTableDataFromChartData } from '../common/common-chart-utils/common-chart-utils';
import { BarLabelWithSmallBarPlacement } from '../common/label/CustomLabel';
import {
  getMarginsBarChart,
  getModifiedXAxisConfigsBarChart,
  getPlotWidthBarChart,
} from './bar-chart-display-utils';
import { barChartStyles } from './bar-chart-styles';
import { BarChartProps } from './bar-chart-types';
import {
  getBarLabelConfig,
  getMuiBarChartDataConfigFromChartData,
} from './bar-chart-utils';

export const BarChart = (props: BarChartProps) => {
  const {
    fullScreenHandle,
    titleConfig,
    displayConfig,
    chartData,
    chartColors = defaultChartColors,
  } = props;
  const selectedSortOrder = displayConfig.enableSortOrder
    ? (displayConfig.selectedSortOrder ?? SortTypes.ASC)
    : null;
  const {
    showLabels = false,
    showTableView = false,
    showLegend = false,
    showGridLines = false,
    showWrappedXAxisLabels = false,
    layout = BarChartLayouts.GROUPED,
  } = displayConfig;
  const showPercentage = layout === BarChartLayouts.PERCENTAGE;
  const chartDataConfig = getMuiBarChartDataConfigFromChartData(chartData, {
    layout,
    selectedSortOrder,
  });
  let tableData = getBarChartTableDataFromChartData(chartData, {
    layout,
    selectedSortOrder,
  });

  const { xAxisConfigs, yAxisConfigs, series, dataset } = chartDataConfig;
  const { t } = useTranslation();

  // Add total column if chart is stackable multi-dataset with showTotal
  const isStackable =
    chartData.type === 'multi-dataset' && chartData.isStackable;
  const showTotal =
    chartData.type === 'multi-dataset' &&
    chartData.isStackable &&
    chartData.showTotal;
  if (showTotal) {
    // Get valueFormatter from first dataset (all datasets should have same formatter)
    const valueFormatter = chartData.datasets[0]?.valueFormatter ?? String;
    tableData = {
      headers: [
        ...tableData.headers,
        { value: t('common:commonValues.misc.total') },
      ],
      rows: tableData.rows.map((row) => {
        // Sum all values for this label from dataMap
        const valuesForLabel = chartData.dataMap[row.title] ?? [];
        const total = sum(valuesForLabel.map((v) => v.value));
        return {
          ...row,
          cells: [
            ...row.cells,
            { key: `${row.key}-total`, value: valueFormatter(total) },
          ],
        };
      }),
    };
  }

  // Stacked segments have no room above them, so only grouped bars move short-bar labels
  const placeSmallLabelsAbove = layout === BarChartLayouts.GROUPED;
  const barLabelConfig = getBarLabelConfig({
    series,
    showPercentage,
    placeSmallLabelsAbove,
  });

  const modifiedDisplayConfig = {
    ...displayConfig,
    enablePercentage: isStackable,
    enableStack: isStackable,
    enableWrappedXAxisLabels: true,
  };

  const [showTools, setShowTools] = useState(false);
  const { elementRef, elementWidth } = useElementWidth();
  const chartRef = useRef<HTMLDivElement>(null);
  const xAxisConfigsInput = {
    isHorizontal: false as const,
    xAxisConfigs: xAxisConfigs,
  };

  const modifiedXAxisConfigs = getModifiedXAxisConfigsBarChart(
    xAxisConfigsInput,
    dataset,
    elementWidth ?? 0,
    {
      wrapLabels: showWrappedXAxisLabels,
      plotWidth: getPlotWidthBarChart(
        yAxisConfigs,
        series,
        dataset,
        elementWidth ?? 0,
        showLegend
      ),
    }
  );

  const muiXAxisConfigs =
    modifiedXAxisConfigs.type === AxisConfigType.SINGLE
      ? [modifiedXAxisConfigs.firstAxisConfig]
      : [
          modifiedXAxisConfigs.firstAxisConfig,
          modifiedXAxisConfigs.secondAxisConfig,
        ];

  const muiYAxisConfigs = yAxisConfigs
    ? yAxisConfigs.type === AxisConfigType.SINGLE
      ? [yAxisConfigs.firstAxisConfig]
      : [yAxisConfigs.firstAxisConfig, yAxisConfigs.secondAxisConfig]
    : undefined;

  const margins = getMarginsBarChart(
    modifiedXAxisConfigs,
    yAxisConfigs,
    series,
    dataset,
    elementWidth ?? 0,
    showLegend
  );
  return (
    <Grid
      item
      container
      ref={elementRef}
      direction="column"
      sx={{ height: '100%' }}
      onMouseEnter={() => setShowTools(true)}
      onMouseLeave={() => setShowTools(false)}
    >
      <ChartHeader
        titleConfig={titleConfig}
        showTools={showTools}
        chartRef={chartRef}
        fullScreenHandle={fullScreenHandle}
        displayConfig={modifiedDisplayConfig}
      />
      <Grid item container sx={{ height: CHART_CONTENT_HEIGHT }}>
        {showTableView ? (
          <TableView
            tableData={tableData}
            fullScreenHandle={fullScreenHandle}
            chartHeight={CHART_CONTENT_HEIGHT}
          />
        ) : (
          <div ref={chartRef} style={{ width: '100%', height: '100%' }}>
            <MuiBarChart
              sx={barChartStyles()}
              title={titleConfig.mainTitle}
              xAxis={muiXAxisConfigs}
              yAxis={muiYAxisConfigs}
              dataset={dataset}
              series={series}
              margin={margins}
              slotProps={{
                popper: { container: () => fullScreenHandle.node.current },
                legend: getChartLegendConfig(showLegend),
                noDataOverlay: getChartNoDataOverlay(t),
              }}
              barLabel={showLabels ? barLabelConfig : undefined}
              slots={
                placeSmallLabelsAbove
                  ? { barLabel: BarLabelWithSmallBarPlacement }
                  : undefined
              }
              grid={{
                vertical: showGridLines,
                horizontal: showGridLines,
              }}
              rightAxis={
                yAxisConfigs?.type === AxisConfigType.DOUBLE
                  ? (yAxisConfigs.secondAxisConfig?.id as string)
                  : undefined
              }
              tooltip={{ trigger: 'axis' }}
              colors={chartColors}
            />
          </div>
        )}
      </Grid>
    </Grid>
  );
};
